import React, { useState } from 'react';
import { CognitiveWarning, RiskLevel } from '../interfaces/types';
import { AlertTriangle, Clock, ChevronDown, ChevronRight, AlertCircle, Check } from 'lucide-react';

interface CognitiveWarningSystemProps {
  warnings: CognitiveWarning[];
  onAcknowledgeWarning?: (warningId: string) => void;
}

const CognitiveWarningSystem: React.FC<CognitiveWarningSystemProps> = ({ 
  warnings,
  onAcknowledgeWarning = () => {}
}) => {
  // State for expanded warnings
  const [expandedWarnings, setExpandedWarnings] = useState<string[]>([]);
  
  // Toggle warning expanded state
  const toggleExpanded = (warningId: string) => {
    setExpandedWarnings(prev => 
      prev.includes(warningId)
        ? prev.filter(id => id !== warningId)
        : [...prev, warningId]
    );
  };
  
  // Format date
  const formatDate = (date: Date): string => {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  
  // Get severity class and icon
  const getSeverityDetails = (severity: RiskLevel): { color: string, bgColor: string, icon: React.ReactNode } => {
    switch (severity) {
      case 'CRITICAL':
        return { 
          color: 'text-red-800', 
          bgColor: 'bg-red-100',
          icon: <AlertCircle className="text-red-500" size={16} />
        };
      case 'HIGH':
        return { 
          color: 'text-orange-800', 
          bgColor: 'bg-orange-100',
          icon: <AlertTriangle className="text-orange-500" size={16} />
        };
      case 'MEDIUM':
        return { 
          color: 'text-yellow-800', 
          bgColor: 'bg-yellow-100',
          icon: <AlertTriangle className="text-yellow-500" size={16} />
        };
      case 'LOW':
        return { 
          color: 'text-blue-800', 
          bgColor: 'bg-blue-100',
          icon: <AlertCircle className="text-blue-500" size={16} />
        };
      default:
        return { 
          color: 'text-gray-800', 
          bgColor: 'bg-gray-100',
          icon: <AlertCircle className="text-gray-500" size={16} />
        };
    }
  };
  
  // Count warnings by severity
  const severityCounts = warnings.reduce((counts, warning) => {
    counts[warning.severity] = (counts[warning.severity] || 0) + 1;
    return counts;
  }, {} as Record<RiskLevel, number>);
  
  // Get count for each severity level, defaulting to 0
  const criticalCount = severityCounts.CRITICAL || 0;
  const highCount = severityCounts.HIGH || 0;
  const mediumCount = severityCounts.MEDIUM || 0;
  const lowCount = severityCounts.LOW || 0;
  
  return (
    <div className="bg-white rounded-lg shadow-sm p-5">
      <div className="flex items-center mb-4">
        <AlertTriangle className="text-red-500 mr-2" />
        <h3 className="text-lg font-semibold">Cognitive Health Early Warning System</h3>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-red-50 rounded-lg p-3 flex flex-col items-center justify-center">
          <span className="text-red-800 text-xs font-medium">CRITICAL</span>
          <span className="text-2xl font-bold text-red-800">{criticalCount}</span>
          <span className="text-xs text-red-600">Immediate attention</span>
        </div>
        
        <div className="bg-orange-50 rounded-lg p-3 flex flex-col items-center justify-center">
          <span className="text-orange-800 text-xs font-medium">HIGH</span>
          <span className="text-2xl font-bold text-orange-800">{highCount}</span>
          <span className="text-xs text-orange-600">Urgent</span>
        </div>
        
        <div className="bg-yellow-50 rounded-lg p-3 flex flex-col items-center justify-center">
          <span className="text-yellow-800 text-xs font-medium">MEDIUM</span>
          <span className="text-2xl font-bold text-yellow-800">{mediumCount}</span>
          <span className="text-xs text-yellow-600">Monitor closely</span>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-3 flex flex-col items-center justify-center">
          <span className="text-blue-800 text-xs font-medium">LOW</span>
          <span className="text-2xl font-bold text-blue-800">{lowCount}</span>
          <span className="text-xs text-blue-600">For awareness</span>
        </div>
      </div>
      
      {warnings.length === 0 ? (
        <div className="bg-green-50 rounded-lg p-6 flex flex-col items-center justify-center">
          <Check size={32} className="text-green-500 mb-2" />
          <h4 className="font-medium text-green-800">No Active Warnings</h4>
          <p className="text-sm text-green-600 text-center mt-1">
            All cognitive health metrics are currently within normal ranges.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {warnings.map(warning => {
            const { color, bgColor, icon } = getSeverityDetails(warning.severity);
            const isExpanded = expandedWarnings.includes(warning.id);
            
            return (
              <div 
                key={warning.id} 
                className={`border rounded-lg overflow-hidden ${warning.isAcknowledged ? 'border-gray-200' : 'border-red-200'}`}
              >
                <div 
                  className={`p-3 cursor-pointer ${
                    warning.isAcknowledged ? 'bg-gray-50' : bgColor
                  }`}
                  onClick={() => toggleExpanded(warning.id)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start">
                      <div className="mr-3 mt-0.5">
                        {isExpanded ? 
                          <ChevronDown size={16} className="text-gray-500" /> : 
                          <ChevronRight size={16} className="text-gray-500" />
                        }
                      </div>
                      <div>
                        <div className="flex items-center">
                          {icon}
                          <span className={`ml-1 font-medium ${warning.isAcknowledged ? 'text-gray-700' : color}`}>
                            {warning.metricType.replace('_', ' ')}
                          </span>
                          {!warning.isAcknowledged && (
                            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                              New
                            </span>
                          )}
                        </div>
                        <p className={`text-sm ${warning.isAcknowledged ? 'text-gray-600' : 'text-gray-800'}`}>
                          {warning.description}
                        </p>
                        <div className="flex items-center mt-1 text-xs text-gray-500">
                          <Clock size={12} className="mr-1" />
                          <span>Detected {formatDate(warning.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        warning.isAcknowledged ? 'bg-gray-100 text-gray-800' : `${bgColor} ${color}`
                      }`}>
                        {warning.severity}
                      </span>
                      <div className="mt-1 text-xs">
                        <span className={warning.trend.direction === 'DECLINING' ? 'text-red-500' : 
                                        warning.trend.direction === 'IMPROVING' ? 'text-green-500' : 
                                        'text-gray-500'}>
                          {warning.trend.direction.toLowerCase()} • {warning.trend.magnitude}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                {isExpanded && (
                  <div className="p-3 border-t bg-white">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 mb-1">Warning Type</h5>
                        <p className="text-sm">{warning.warningType.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 mb-1">Confidence</h5>
                        <p className="text-sm">{warning.confidence}%</p>
                      </div>
                      {warning.relatedMetrics.length > 0 && (
                        <div>
                          <h5 className="text-xs font-medium text-gray-500 mb-1">Related Metrics</h5>
                          <div className="flex flex-wrap gap-1">
                            {warning.relatedMetrics.map((metric, idx) => (
                              <span 
                                key={idx} 
                                className="px-2 py-0.5 bg-gray-100 rounded-full text-xs"
                              >
                                {metric}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div>
                        <h5 className="text-xs font-medium text-gray-500 mb-1">Trend</h5>
                        <p className="text-sm">
                          {warning.trend.direction} over {warning.trend.duration} days 
                          ({warning.trend.magnitude}% change)
                        </p>
                      </div>
                    </div>
                    
                    {warning.recommendedActions && warning.recommendedActions.length > 0 && (
                      <div className="mb-3">
                        <h5 className="text-xs font-medium text-gray-500 mb-1">Recommended Actions</h5>
                        <ul className="text-sm space-y-1 list-disc list-inside">
                          {warning.recommendedActions.map((action, idx) => (
                            <li key={idx}>{action}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {!warning.isAcknowledged && (
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onAcknowledgeWarning(warning.id);
                          }}
                          className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs font-medium text-gray-700 transition-colors"
                        >
                          Acknowledge Warning
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CognitiveWarningSystem; 